import { create } from "zustand";
import { persist } from "zustand/middleware";

import { MessageKind, MessageType } from "@/types/enums/message";
import type { MessageContext } from "@/types/message";

import {
  createTenantChangeListener,
  createTenantStorage,
} from "./tenant-storage-helpers";

export interface MessageDraft {
  chatId: string;
  content: string;
  lastUpdated: string;
  message: MessageContext;
}

interface MessageDraftStore {
  drafts: Record<string, MessageDraft>;
  getDraft: (chatId: string) => MessageDraft | null;
  clearDraft: (chatId: string) => void;
  useDraftContent: (chatId: string | null | undefined) => string;
  handleDraftChange: (
    chatId: string | null | undefined,
    content: string,
    message: MessageContext,
  ) => void;
  clearDraftOnSend: (chatId: string | null | undefined) => void;
}

export const useMessageDraftStore = create<MessageDraftStore>()(
  persist(
    (set, get) => ({
      drafts: {},
      getDraft: (chatId) => get().drafts[chatId] ?? null,
      clearDraft: (chatId) =>
        set((state) => {
          const drafts = { ...state.drafts };
          delete drafts[chatId];
          return { drafts };
        }),
      useDraftContent: (chatId) =>
        chatId ? (get().drafts[chatId]?.content ?? "") : "",
      handleDraftChange: (chatId, content, message) => {
        if (!chatId) return;
        set((state) => ({
          drafts: {
            ...state.drafts,
            [chatId]: {
              chatId,
              content,
              message,
              lastUpdated: new Date().toISOString(),
            },
          },
        }));
      },
      clearDraftOnSend: (chatId) => {
        if (chatId) get().clearDraft(chatId);
      },
    }),
    {
      name: "message-draft-store",
      partialize: (state) => ({ drafts: state.drafts }),
      storage: createTenantStorage(),
      version: 2,
      migrate: (persisted) => {
        const old = persisted as {
          drafts?: Record<
            string,
            {
              chatId: string;
              content: string;
              lastUpdated: string;
              quoteId?: string;
              eventId?: string;
              messageType?: MessageType;
              waitlistEntryIds?: string[];
            }
          >;
        };
        const drafts: Record<string, MessageDraft> = {};
        for (const [chatId, draft] of Object.entries(old.drafts ?? {})) {
          const [firstId, ...otherIds] = draft.waitlistEntryIds ?? [];
          const message: MessageContext = firstId
            ? {
                kind: MessageKind.WAITLIST,
                waitlistEntryIds: [firstId, ...otherIds],
              }
            : draft.eventId
              ? {
                  kind: MessageKind.EVENT,
                  eventId: draft.eventId,
                  messageType:
                    draft.messageType === MessageType.WAITLIST
                      ? undefined
                      : draft.messageType,
                  quoteId: draft.quoteId,
                }
              : { kind: MessageKind.DEFAULT, quoteId: draft.quoteId };
          drafts[chatId] = {
            chatId,
            content: draft.content,
            lastUpdated: draft.lastUpdated,
            message,
          };
        }
        return { drafts };
      },
    },
  ),
);

createTenantChangeListener("message-draft-store", (tenantData) => {
  // Rehydrate through persist so each tenant's old data runs the version migration.
  if (tenantData.drafts) void useMessageDraftStore.persist.rehydrate();
  else useMessageDraftStore.setState({ drafts: {} });
});
